import React, { useState } from "react";
import { CheckCircle2, Bookmark, Link as LinkIcon, AlertTriangle, Sun, Undo2, Trash2, ShieldCheck, Rocket } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errText } from "../lib/api";

/**
 * Small inline confirmation chip rendered inside an assistant message when
 * Life Assistant ran an internal side effect (task/memory).
 * Values are read from `meta.chip` on the message. Never renders secrets.
 */
export default function IntentChip({ chip, onChanged }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (!chip || !chip.kind) return null;

  const wrapper = "mt-2 inline-flex items-start gap-2 text-xs bg-white border border-[#E5E4DE] rounded-xl px-3 py-2 max-w-full";
  const dot = "shrink-0 mt-0.5";

  if (chip.kind === "task_created") {
    return (
      <div className={wrapper} data-testid="chip-task-created">
        <CheckCircle2 className={`${dot} w-4 h-4 text-emerald-600`} />
        <div className="min-w-0">
          <div className="text-slate-900"><span className="font-semibold">Task created:</span> {chip.title}</div>
          <div className="text-slate-500 mt-0.5">
            {chip.due_date && <>Due {chip.due_date.slice(0, 16).replace("T", " ")} · </>}
            priority {chip.priority || "med"}
          </div>
          <div className="mt-1 flex gap-3">
            <button className="text-indigo-700 font-medium hover:underline"
                    data-testid={`chip-task-undo-${chip.task_id}`}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await api.delete(`/tasks/${chip.task_id}`);
                        toast.success("Task undone");
                        onChanged?.();
                      } catch (err) { toast.error(errText(err, "Undo failed")); }
                      finally { setBusy(false); }
                    }}>
              <Undo2 className="inline w-3 h-3 mr-0.5" /> Undo
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "memory_saved") {
    return (
      <div className={wrapper} data-testid="chip-memory-saved">
        <Bookmark className={`${dot} w-4 h-4 text-indigo-600`} />
        <div className="min-w-0">
          <div className="text-slate-900"><span className="font-semibold">Remembered:</span> {chip.content}</div>
          <div className="mt-1 flex gap-3">
            <button className="text-red-700 font-medium hover:underline"
                    data-testid={`chip-memory-forget-${chip.memory_id}`}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await api.delete(`/memories/${chip.memory_id}`);
                        toast.success("Memory removed");
                        onChanged?.();
                      } catch (err) { toast.error(errText(err, "Could not remove")); }
                      finally { setBusy(false); }
                    }}>
              <Trash2 className="inline w-3 h-3 mr-0.5" /> Forget
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "memory_sensitive_pending") {
    const cat = chip.sensitive_category || "private";
    const catLabels = {
      private: "Private", family: "Family", visa_immigration: "Visa & Immigration",
      finance: "Finance", legal: "Legal", medical: "Medical", credential: "Credential",
    };
    // Credential-category content is deny-listed server-side; we never allow save.
    const canSave = cat !== "credential";
    const saveMemory = async () => {
      if (!canSave) return;
      setBusy(true);
      try {
        const typeMap = {
          family: "user_preference", visa_immigration: "user_preference",
          finance: "user_preference", legal: "user_preference",
          medical: "user_preference", private: "private_preference",
        };
        await api.post("/memories", {
          type: typeMap[cat] || "user_preference",
          content: chip.content,
          source: "chat",
          context_scope: "private",
          sensitive: true,
          confirm_sensitive: true,
        });
        toast.success("Saved to Memory");
        onChanged?.();
      } catch (err) {
        toast.error(errText(err, "Could not save"));
      } finally { setBusy(false); }
    };
    return (
      <div className={`${wrapper} border-amber-200 bg-amber-50`} data-testid="chip-memory-sensitive">
        <AlertTriangle className={`${dot} w-4 h-4 text-amber-700`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-amber-800 font-semibold">Do you want me to remember this?</span>
            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5 font-medium inline-flex items-center gap-1"
                  data-testid="chip-sensitive-badge">
              <ShieldCheck className="w-2.5 h-2.5" /> Sensitive · {catLabels[cat] || cat}
            </span>
          </div>
          <div className="text-amber-700 mt-0.5 text-[11px] line-clamp-2">"{chip.content}"</div>
          {!canSave && (
            <div className="text-red-700 mt-1 text-[11px]">
              This looks like a credential. Stella will not save credentials or secrets.
            </div>
          )}
          <div className="mt-2 flex gap-3 items-center">
            <button className="text-indigo-700 font-medium hover:underline disabled:opacity-50"
                    data-testid="chip-memory-remember"
                    disabled={busy || !canSave}
                    onClick={saveMemory}>
              <Bookmark className="inline w-3 h-3 mr-0.5" /> Remember
            </button>
            <button className="text-slate-600 font-medium hover:underline"
                    data-testid="chip-memory-no"
                    onClick={() => toast.message("Not saved", { description: "This memory was not stored." })}>
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "task_completed") {
    return (
      <div className={wrapper} data-testid="chip-task-completed">
        <CheckCircle2 className={`${dot} w-4 h-4 text-emerald-600`} />
        <div className="min-w-0">
          <div className="text-slate-900"><span className="font-semibold">Marked done:</span> {chip.title}</div>
        </div>
      </div>
    );
  }

  if (chip.kind === "complete_candidates" || chip.kind === "forget_candidates") {
    const isComplete = chip.kind === "complete_candidates";
    return (
      <div className={wrapper} data-testid={`chip-${chip.kind}`}>
        <AlertTriangle className={`${dot} w-4 h-4 text-amber-600`} />
        <div className="min-w-0">
          <div className="text-slate-800">
            Multiple matches for "{chip.query}". Which one?
          </div>
          <ul className="mt-1 space-y-0.5 text-slate-700">
            {chip.candidates?.map((c) => (
              <li key={c.id} className="truncate">• {c.title || c.content}</li>
            ))}
          </ul>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {isComplete ? "Open Tasks to complete the right one." : "Open Memory to remove the right one."}
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "complete_none") {
    return (
      <div className={wrapper} data-testid="chip-complete-none">
        <AlertTriangle className={`${dot} w-4 h-4 text-slate-500`} />
        <div className="text-slate-700">Couldn't find an open task matching "{chip.query}".</div>
      </div>
    );
  }
  if (chip.kind === "forget_none") {
    return (
      <div className={wrapper} data-testid="chip-forget-none">
        <AlertTriangle className={`${dot} w-4 h-4 text-slate-500`} />
        <div className="text-slate-700">No memory matched "{chip.query}".</div>
      </div>
    );
  }

  if (chip.kind === "plan_today") {
    return (
      <div className={wrapper} data-testid="chip-plan-today">
        <Sun className={`${dot} w-4 h-4 text-amber-500`} />
        <div className="text-slate-800">Pulling in your open tasks and follow-ups…</div>
      </div>
    );
  }

  if (chip.kind === "warn") {
    return (
      <div className={`${wrapper} border-amber-200 bg-amber-50`} data-testid="chip-warn">
        <AlertTriangle className={`${dot} w-4 h-4 text-amber-700`} />
        <div className="text-amber-800">{chip.message}</div>
      </div>
    );
  }

  if (chip.kind === "mission_proposal") {
    const propose = async () => {
      if (chip.mission_id) {
        nav(`/missions/${chip.mission_id}`);
        return;
      }
      setBusy(true);
      try {
        const { data } = await api.post("/missions", {
          title: chip.proposed_title || "New mission",
          objective: chip.proposed_objective || "",
        });
        toast.success("Mission draft created — review the scope");
        nav(`/missions/${data.id}`);
      } catch (err) {
        toast.error(errText(err, "Could not create mission"));
      } finally { setBusy(false); }
    };
    return (
      <div className={`${wrapper} border-indigo-200 bg-indigo-50/60`} data-testid="chip-mission-proposal">
        <Rocket className={`${dot} w-4 h-4 text-indigo-700`} />
        <div className="min-w-0">
          <div className="text-slate-900">
            <span className="font-semibold">{chip.mission_id ? "Mission created." : "Sounds like a mission."}</span> Review
            "<span className="italic">{chip.proposed_title}</span>"? Review scope →
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Nothing external runs until you authorize the mission scope.
          </div>
          <div className="mt-1 flex gap-3">
            <button className="text-indigo-700 font-medium hover:underline disabled:opacity-50"
                    data-testid="chip-mission-propose"
                    disabled={busy}
                    onClick={propose}>
              Create mission & review →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "approval_clarify") {
    return (
      <div className={wrapper} data-testid="chip-approval-clarify">
        <AlertTriangle className={`${dot} w-4 h-4 text-amber-600`} />
        <div className="min-w-0">
          <div className="text-slate-900">
            <span className="font-semibold">Which account should I draft from?</span>
          </div>
          <div className="text-slate-500 mt-0.5 text-[11px]">
            Stella has more than one connected email account. Choose one and I'll queue the draft in Approvals.
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(chip.options || []).map((o) => (
              <Link
                key={o.id}
                to={`/approvals`}
                className="inline-flex items-center gap-1 text-[11px] font-medium bg-white border border-[#E5E4DE] hover:border-indigo-300 rounded-full pl-2 pr-2 py-0.5 text-slate-700"
                data-testid={`chip-clarify-option-${o.id}`}
              >
                <span className="uppercase tracking-wider text-[9px] text-slate-500">
                  {o.account_label || o.account_category || "account"}
                </span>
                <span className="opacity-40">·</span>
                <span className="truncate max-w-[220px]">{o.email_address}</span>
              </Link>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            To pick an account, reply with e.g. <em>"send from work"</em> or <em>"from &lt;email&gt;"</em>. Nothing is drafted yet.
          </div>
        </div>
      </div>
    );
  }

  if (chip.kind === "approval_queued") {
    const labels = {
      email_send: "Email — send",
      email_reply: "Email — reply",
      email_forward: "Email — forward",
      calendar_create: "Calendar — create event",
      calendar_update: "Calendar — reschedule event",
      calendar_delete: "Calendar — cancel event",
      file_share: "File — share",
    };
    const label = labels[chip.action_type] || "External action";
    return (
      <div className={wrapper} data-testid="chip-approval-queued">
        <ShieldCheck className={`${dot} w-4 h-4 text-indigo-600`} />
        <div className="min-w-0">
          <div className="text-slate-900">
            <span className="font-semibold">Queued for approval:</span> {label}
          </div>
          {chip.target && (
            <div className="text-slate-500 mt-0.5 truncate">→ {chip.target}</div>
          )}
          <div className="text-[11px] text-slate-500 mt-1">
            Nothing has been sent, scheduled, or executed. Review it in the Approvals queue.
          </div>
          <div className="mt-1 flex gap-3">
            <Link to="/approvals"
                  className="text-indigo-700 font-medium hover:underline"
                  data-testid={`chip-approval-open-${chip.approval_id}`}>
              Open Approvals →
            </Link>
            <button className="text-red-700 font-medium hover:underline"
                    data-testid={`chip-approval-reject-${chip.approval_id}`}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await api.post(`/approvals/${chip.approval_id}/reject`);
                        toast.success("Approval rejected");
                        onChanged?.();
                      } catch (err) { toast.error(errText(err, "Reject failed")); }
                      finally { setBusy(false); }
                    }}>
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
